import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { IconAsterisk, IconChevronDownSmall } from '~/ui/core/icons';
import { RecessedSurface } from '~/ui/core/recessed-surface/RecessedSurface';
import { PromptComposer } from '~/ui/domain/PromptComposer/PromptComposer';

// Deep-import (not via the chat barrel): the chat barrel pulls ChatBody, whose
// block renderers import job-architecture-v2 modules — a module-eval cycle.
import { useChatComposerCallbacks } from '../../chat/chat-composer-contexts';
import { CommentMessage } from '../../comments/CommentMessage';
import { Spinner } from '../../components/Spinner';
import { TranscribableTextarea } from '../../components/transcription/TranscribableTextarea';
import type { ProfileRegenerateMessageContent, RegenerateScope } from '../../data-contract/chat';
import { useMixpanelTracking } from '../../tracking/mixpanel/use-mixpanel-tracking';
import { useJsaTrackingContext } from '../hooks/analytics/use-jsa-tracking-context';
import { SwitchesWrapper } from './PopoverStyles';
import { useRegenerateCommentsAvailability } from './use-regenerate-comments-availability';

export type RegenerateDialogProps = {
    readonly architectureId: string;
    readonly children: (isSubmitting: boolean) => ReactNode;
    readonly isOpen: boolean;
    readonly itemId?: string;
    readonly itemName: string;
    readonly onOpenChange: (open: boolean) => void;
    readonly parentRoleId?: string;
    readonly version: string | number;
} & (
    | { readonly itemType: 'position'; readonly language: string }
    | { readonly itemType: 'job-level' | 'job-role'; readonly language?: never }
);

const REGENERATE_TYPE = {
    'job-level': 'level',
    'job-role': 'role',
    position: 'position',
} as const satisfies Record<RegenerateDialogProps['itemType'], ProfileRegenerateMessageContent['type']>;

const getCommentsOptionCardClassName = ({
    disabled,
    selected,
}: {
    readonly disabled: boolean;
    readonly selected: boolean;
}): string =>
    clsx(
        'box-border flex w-full items-center justify-between gap-m rounded-lg border-0 px-l py-xs',
        'bg-transparent shadow-none transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
        selected ? 'text-text-primary' : 'text-text-content-strong',
    );

const getOptionRowClassName = ({
    disabled,
    selected,
}: {
    readonly disabled: boolean;
    readonly selected: boolean;
}): string =>
    clsx(
        'box-border flex min-h-[calc(var(--spacing-5xl)+var(--spacing-xs)*2)] w-full items-center gap-s rounded-lg border-0 bg-transparent px-l py-xs shadow-none transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
        selected ? 'text-text-primary' : 'text-text-content-strong',
    );

const getCommentsOptionLabelClassName = (selected: boolean): string =>
    clsx('truncate text-left text-body-s font-medium', selected ? 'text-text-primary' : 'text-text-content-strong');

const getOptionLabelClassName = (selected: boolean): string =>
    clsx('text-body-s font-medium', selected ? 'text-text-primary' : 'text-text-content-strong');

const getCommentsBadgeVariant = (selected: boolean): 'gray' | 'orange' => (selected ? 'orange' : 'gray');

const getCommentPreviewsToggleIconClassName = (isOpen: boolean): string =>
    clsx('transition-transform duration-200', isOpen && 'rotate-180');

const commentsPreviewFadeClassName =
    'pointer-events-none absolute inset-x-0 bottom-0 h-xl bg-gradient-to-t from-surface-raised to-surface-raised/0';

const resolveScope = ({
    includeDescription,
    includeSkills,
}: {
    readonly includeDescription: boolean;
    readonly includeSkills: boolean;
}): RegenerateScope => {
    if (includeDescription && includeSkills) {
        return 'all';
    }

    return includeDescription ? 'description' : 'skills';
};

type TrackingIds = { readonly levelId?: string; readonly positionId?: string; readonly roleId?: string };

const OptionSurface: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <RecessedSurface
        active={false}
        hoverActive={true}
        hoverPocketTone="neutral"
        hoverSurfaceShadow="sm"
        hoverSurfaceTone="white"
        inactiveSurfaceShadow="sm"
        inactiveSurfaceTone="white"
        placement="outside"
        radius="md"
        renderMode="overlay"
        transition="none"
    >
        {children}
    </RecessedSurface>
);

const ApplyTargetOption: FunctionComponent<{
    readonly disabled: boolean;
    readonly label: string;
    readonly selected: boolean;
    readonly onToggle: () => void;
}> = ({ disabled, label, selected, onToggle }) => (
    <OptionSurface>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: checkbox stays keyboard-accessible; this extends the pointer hit area across the row */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: row delegates pointer activation to the actual checkbox state */}
        <div className={getOptionRowClassName({ disabled, selected })} onClick={onToggle}>
            <Checkbox
                aria-label={label}
                checked={selected}
                className="!transition-none data-[state=checked]:!bg-accent"
                disabled={disabled}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onCheckedChange={onToggle}
            />
            <span className={getOptionLabelClassName(selected)}>{label}</span>
        </div>
    </OptionSurface>
);

export const RegenerateDialog: FunctionComponent<RegenerateDialogProps> = ({
    architectureId,
    children,
    isOpen,
    itemId,
    itemName,
    itemType,
    language,
    onOpenChange,
    parentRoleId,
    version,
}) => {
    const translate = useTranslate();
    const { sendMessage } = useChatComposerCallbacks();
    const [additionalPrompt, setAdditionalPrompt] = useState('');
    const [includeDescription, setIncludeDescription] = useState(true);
    const [includeSkills, setIncludeSkills] = useState(true);
    const [includeComments, setIncludeComments] = useState(false);
    const [includeReplies, setIncludeReplies] = useState(false);
    const [areCommentPreviewsOpen, setAreCommentPreviewsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();
    const { commentCount, comments, isAvailable: areCommentsAvailable } = useRegenerateCommentsAvailability();
    // Comments only steer the drafted description, so they are inert for a skills-only run.
    const areCommentsApplicable = itemType !== 'position' && areCommentsAvailable && includeDescription;

    useEffect(() => {
        if (!areCommentsApplicable) {
            setIncludeComments(false);
        }
    }, [areCommentsApplicable]);

    useEffect(() => {
        if (!includeComments) {
            setIncludeReplies(false);
        }
    }, [includeComments]);

    // Submitting is one-way: the popover closes right after the send, so the guard is only
    // released when the dialog is opened again. Clearing it inside handleSubmit would let a
    // rapid second click send the same regenerate request twice.
    useEffect(() => {
        if (isOpen) {
            setIsSubmitting(false);
        }
    }, [isOpen]);

    // Radix moves focus to the popover surface on open; focusing the textarea has to happen after that.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const frame = requestAnimationFrame(() => {
            promptRef.current?.focus({ preventScroll: true });
        });

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [isOpen]);

    const getTrackingIds = (): TrackingIds => {
        if (itemType === 'position') {
            return { positionId: itemId };
        }
        if (itemType === 'job-role') {
            return { roleId: itemId };
        }
        if (itemType === 'job-level') {
            return { levelId: itemId, roleId: parentRoleId };
        }

        return {};
    };

    const trackRegenerationActions = (trackingIds: TrackingIds): void => {
        if (includeDescription) {
            trackEvent({
                name: 'ai-action-used',
                payload: {
                    ...trackingContext,
                    ...trackingIds,
                    actionScope: 'section',
                    actionType: 'regenerate_description',
                    architectureId,
                    section: 'description',
                },
            });
        }
        if (includeSkills) {
            trackEvent({
                name: 'ai-action-used',
                payload: {
                    ...trackingContext,
                    ...trackingIds,
                    actionScope: 'skills',
                    actionType: 'suggest_skills',
                    architectureId,
                },
            });
        }
    };

    const isSubmitDisabled = !includeDescription && !includeSkills;
    const scope = resolveScope({ includeDescription, includeSkills });

    const handleSubmit = (): void => {
        if (isSubmitting || isSubmitDisabled || is.nullOrUndefined(itemId)) {
            return;
        }

        trackRegenerationActions(getTrackingIds());
        setIsSubmitting(true);

        sendMessage('', {
            content: {
                id: itemId,
                ...(itemType === 'position' ? { language } : { includeComments, includeReplies }),
                name: itemName,
                prompt: additionalPrompt.trim(),
                scope,
                type: REGENERATE_TYPE[itemType],
                version,
            },
            messageType: 'regenerate',
        });
        onOpenChange(false);
        setAdditionalPrompt('');
    };

    const updateIncludeComments = (shouldIncludeComments: boolean): void => {
        if (!isSubmitting) {
            setIncludeComments(shouldIncludeComments);
            setIncludeReplies(shouldIncludeComments);
        }
    };

    const toggleIncludeComments = (): void => {
        updateIncludeComments(!includeComments);
    };

    const toggleIncludeReplies = (): void => {
        if (!isSubmitting) {
            setIncludeReplies((current) => !current);
        }
    };

    const handleCancel = (): void => {
        onOpenChange(false);
    };

    const submitLabel = (
        <Fragment>
            {isSubmitting ? <Spinner /> : <IconAsterisk className="size-[16px] text-accent" />}
            {translate('pages.job-architecture-v2.job-profile-regenerate')}
        </Fragment>
    );

    return (
        <PromptComposer
            align="end"
            anchorOnly={true}
            cancelLabel={translate('common.cancel')}
            isOpen={isOpen}
            isSubmitting={isSubmitting}
            mode="popover"
            submitDisabled={isSubmitDisabled}
            submitLabel={submitLabel}
            trigger={children(isSubmitting)}
            width="456px"
            onCancel={handleCancel}
            onSubmit={handleSubmit}
        >
            <div className="flex w-full flex-col gap-m">
                <OptionSurface>
                    <TranscribableTextarea
                        disabled={isSubmitting}
                        ref={promptRef}
                        placeholder={translate(
                            'pages.job-architecture-v2.regenerate-dialog.additional-prompt-placeholder',
                        )}
                        rows={8}
                        textareaClassName="!border-none !shadow-none"
                        value={additionalPrompt}
                        onKeyDown={(event) => {
                            // Enter confirms the candidate while an IME is composing — it must not submit.
                            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                                event.preventDefault();
                                handleSubmit();
                            }
                        }}
                        onValueChange={setAdditionalPrompt}
                    />
                </OptionSurface>
                <div className="flex w-full flex-col gap-xs">
                    <span className="text-body-s font-medium text-text-content-strong">
                        {translate('pages.job-architecture-v2.regenerate-dialog.apply-target')}
                    </span>
                    <div className="flex w-full flex-col gap-s">
                        <ApplyTargetOption
                            disabled={isSubmitting}
                            label={translate('pages.job-architecture-v2.regenerate-dialog.apply-to-description')}
                            selected={includeDescription}
                            onToggle={() => {
                                if (!isSubmitting) {
                                    setIncludeDescription((current) => !current);
                                }
                            }}
                        />
                        <ApplyTargetOption
                            disabled={isSubmitting}
                            label={translate('pages.job-architecture-v2.regenerate-dialog.apply-to-skills')}
                            selected={includeSkills}
                            onToggle={() => {
                                if (!isSubmitting) {
                                    setIncludeSkills((current) => !current);
                                }
                            }}
                        />
                    </div>
                </div>
                {areCommentsApplicable ? (
                    <SwitchesWrapper>
                        <div className="flex w-full flex-col gap-s">
                            <OptionSurface>
                                {/* biome-ignore lint/a11y/useKeyWithClickEvents: checkbox remains keyboard-accessible; this extends the pointer hit area across the selector surface */}
                                {/* biome-ignore lint/a11y/noStaticElementInteractions: selector surface delegates pointer activation to the actual checkbox state */}
                                <div
                                    className={getCommentsOptionCardClassName({
                                        disabled: isSubmitting,
                                        selected: includeComments,
                                    })}
                                    onClick={toggleIncludeComments}
                                >
                                    <span className="inline-flex shrink-0 items-center gap-s">
                                        <Checkbox
                                            aria-label={translate(
                                                'pages.job-architecture-v2.regenerate-dialog.regenerate-with-comments',
                                            )}
                                            checked={includeComments}
                                            className="!transition-none data-[state=checked]:!bg-accent"
                                            disabled={isSubmitting}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                            }}
                                            onCheckedChange={(checked) => {
                                                updateIncludeComments(checked === true);
                                            }}
                                        />
                                    </span>
                                    <span className="flex min-w-0 flex-1 items-center justify-between gap-s">
                                        <span className={getCommentsOptionLabelClassName(includeComments)}>
                                            {translate(
                                                'pages.job-architecture-v2.regenerate-dialog.regenerate-with-comments',
                                            )}
                                        </span>
                                        <Badge className="shrink-0" variant={getCommentsBadgeVariant(includeComments)}>
                                            {translate('pages.job-architecture-v2.regenerate-dialog.comment-count', {
                                                count: commentCount,
                                            })}
                                        </Badge>
                                    </span>
                                    <Button
                                        aria-expanded={areCommentPreviewsOpen}
                                        aria-label={translate('common.more')}
                                        className="!text-icon-muted"
                                        iconOnly={true}
                                        size="sm"
                                        type="button"
                                        variant="tertiary"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setAreCommentPreviewsOpen((current) => !current);
                                        }}
                                    >
                                        <IconChevronDownSmall
                                            className={getCommentPreviewsToggleIconClassName(areCommentPreviewsOpen)}
                                        />
                                    </Button>
                                </div>
                                {areCommentPreviewsOpen ? (
                                    <div className="relative">
                                        <div className="flex max-h-[168px] w-full flex-col gap-s overflow-auto px-s pb-s">
                                            {comments.map((comment) => (
                                                <div
                                                    className="min-w-0 rounded-md bg-comment-card-surface p-xl shadow-sm"
                                                    key={comment.id}
                                                >
                                                    <CommentMessage
                                                        author={comment.author}
                                                        content={comment.content}
                                                        createdAt={comment.createdAt}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className={commentsPreviewFadeClassName} />
                                    </div>
                                ) : null}
                            </OptionSurface>
                            {includeComments ? (
                                <OptionSurface>
                                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: checkbox stays keyboard-accessible; this extends the pointer hit area across the row */}
                                    {/* biome-ignore lint/a11y/noStaticElementInteractions: row delegates pointer activation to the actual checkbox state */}
                                    <div
                                        className={getOptionRowClassName({
                                            disabled: isSubmitting,
                                            selected: includeReplies,
                                        })}
                                        onClick={toggleIncludeReplies}
                                    >
                                        <Checkbox
                                            aria-label={translate(
                                                'pages.job-architecture-v2.regenerate-dialog.include-replies',
                                            )}
                                            checked={includeReplies}
                                            className="!transition-none data-[state=checked]:!bg-accent"
                                            disabled={isSubmitting}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                            }}
                                            onCheckedChange={(checked) => {
                                                setIncludeReplies(checked === true);
                                            }}
                                        />
                                        <span className={getOptionLabelClassName(includeReplies)}>
                                            {translate('pages.job-architecture-v2.regenerate-dialog.include-replies')}
                                        </span>
                                    </div>
                                </OptionSurface>
                            ) : null}
                        </div>
                    </SwitchesWrapper>
                ) : null}
            </div>
        </PromptComposer>
    );
};
